import { Briefcase, MapPin } from "@phosphor-icons/react";

import {
  MobileSelectionListItem,
  PaginatedMobileSelectionList,
} from "common/components/MobileSelectionList";
import ProfileImage from "common/components/ui/ProfileImage";
import { Tag } from "common/components/ui/Tag";
import type { LinkedInProfile } from "common/types";

import type { Icon } from "@phosphor-icons/react";
import type { BlacklistCompanyRow } from "../DesktopLayout/BlacklistCompaniesTable";

export const BLACKLIST_ACCOUNTS_MOBILE_TAGS: Record<
  string,
  {
    icon: Icon;
    label: string;
    renderTag: (profile: LinkedInProfile) => JSX.Element;
    isDefault?: true;
  }
> = {
  currentCompany: {
    icon: Briefcase,
    label: "Current company",
    isDefault: true,
    renderTag: ({ company }) => (
      <Tag size="sm" variant="tertiary" leftIcon={<Briefcase weight="fill" />}>
        {company || "—"}
      </Tag>
    ),
  },
  location: {
    icon: MapPin,
    label: "Location",
    isDefault: true,
    renderTag: ({ location }) => (
      <Tag size="sm" variant="tertiary" leftIcon={<MapPin weight="fill" />}>
        {location?.name ?? "—"}
      </Tag>
    ),
  },
};

interface BlacklistAccountsMobileListProps {
  blacklistType: "accounts";
  profiles: LinkedInProfile[];
  selectedProfiles: LinkedInProfile[];
  setSelectedProfiles: (rows: LinkedInProfile[]) => void;
}

interface BlacklistCompaniesMobileListProps {
  blacklistType: "companies";
  companies: BlacklistCompanyRow[];
  selectedCompanies: BlacklistCompanyRow[];
  setSelectedCompanies: (rows: BlacklistCompanyRow[]) => void;
}

type BlacklistMobileListProps =
  | BlacklistAccountsMobileListProps
  | BlacklistCompaniesMobileListProps;

export default function BlacklistMobileList(props: BlacklistMobileListProps) {
  if (props.blacklistType === "accounts") {
    const { profiles, selectedProfiles, setSelectedProfiles } = props;

    return (
      <PaginatedMobileSelectionList
        title="Accounts"
        data={profiles}
        localStorageId="blacklist-accounts"
        selection={selectedProfiles}
        onSelectionChange={(newSelection) => {
          if (newSelection === "all") {
            setSelectedProfiles(profiles);
          } else {
            setSelectedProfiles(newSelection);
          }
        }}
        allItems={BLACKLIST_ACCOUNTS_MOBILE_TAGS}
      >
        {(profile, listItemProps) => (
          <MobileSelectionListItem {...listItemProps}>
            <ProfileImage size="sm" src={profile.picture_url} />

            <div className="flex flex-col gap-0.5 min-w-0 flex-1 text-left">
              <span className="text-xs-bold truncate">{profile.full_name}</span>

              <span className="line-clamp-1 text-button-xxs text-black-500">
                {profile.occupation}
              </span>
            </div>
          </MobileSelectionListItem>
        )}
      </PaginatedMobileSelectionList>
    );
  }

  const { companies, selectedCompanies, setSelectedCompanies } = props;

  function onSelectionChange(newSelection: "all" | BlacklistCompanyRow[]) {
    setSelectedCompanies(newSelection === "all" ? companies : newSelection);
  }

  return (
    <PaginatedMobileSelectionList
      title="Companies"
      data={companies}
      localStorageId="blacklist-companies"
      selection={selectedCompanies}
      onSelectionChange={onSelectionChange}
    >
      {(company, listItemProps) => (
        <MobileSelectionListItem {...listItemProps}>
          <ProfileImage size="sm" src={company.logo_url} />

          <div className="flex flex-col gap-0.5 min-w-0 flex-1 text-left">
            <span className="text-xs-bold truncate">{company.name}</span>

            <span className="line-clamp-1 text-button-xxs text-black-500">
              {company.description}
            </span>
          </div>
        </MobileSelectionListItem>
      )}
    </PaginatedMobileSelectionList>
  );
}
